import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/seo/Seo';

const CATEGORY_COUNT = 6;
const CATEGORY_ITEMS = [5, 3, 3, 2, 3, 2];

function renderParagraphs(text: string, className: string) {
  return text.split(/(?:\r?\n){2,}/).filter((para) => para.length > 0).map((para, i) => (
    <p key={i} className={className}>
      {para}
    </p>
  ));
}

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const { lang } = useLanguageContext();
  const { t } = useTranslation();

  const toggle = (id: string) => {
    setOpenIndex(openIndex === id ? null : id);
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: lang === 'en' ? 'Home' : 'ホーム', item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: lang === 'en' ? 'FAQ' : 'よくある質問', item: 'https://butler-academy.jp/faq' },
    ],
  };

  const faqItems = Array.from({ length: CATEGORY_COUNT }, (_, catIdx) =>
    Array.from({ length: CATEGORY_ITEMS[catIdx] }, (_, idx) => ({
      question: t(`faq.cat${catIdx}.${idx}.question`),
      answer: String(t(`faq.cat${catIdx}.${idx}.answer`)).replace(/[\r\n]+/g, ' '),
    }))
  ).flat();

  return (
    <>
      <Seo route="/faq" jsonLd={breadcrumbData} faqItems={faqItems} />


      <Header />

      <main>
        {/* Hero */}
        <section className="relative bg-primary-900 py-24 md:py-32">
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(ellipse_at_top,oklch(var(--accent-500)),transparent_60%)]"></div>
          <div className="relative z-10 max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 text-center">
            {renderParagraphs(String(t('faq.hero.titleEn')), 'text-accent-400/70 text-[13px] tracking-[0.2em] mb-6 font-label')}
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-100 tracking-[0.06em] mb-4">
              {t('faq.hero.title')}
            </h1>
            <p className="section-title-en text-accent-400/60 text-[15px] tracking-[0.12em] mb-6">
              Frequently Asked Questions
            </p>
            <div className="gold-line mx-auto mb-8"></div>
            {renderParagraphs(String(t('faq.hero.lead')), 'text-[16px] text-foreground-400 leading-relaxed tracking-[0.04em] max-w-lg mx-auto')}
          </div>
        </section>
      <Breadcrumb items={[{ label: 'よくある質問', labelEn: 'FAQ' }]} />

        {/* FAQ Categories */}
        <section className="py-20 md:py-28">
          <div className="max-w-[800px] mx-auto px-8">
            <div className="space-y-16">
              {Array.from({ length: CATEGORY_COUNT }, (_, catIdx) => (
                <div key={catIdx}>
                  <h2 className="font-heading text-lg text-foreground-900 tracking-[0.06em] mb-6 pb-3 border-b border-accent-500/20">
                    {t(`faq.category.${catIdx}`)}
                  </h2>
                  <div className="space-y-3">
                    {Array.from({ length: CATEGORY_ITEMS[catIdx] }, (_, idx) => {
                      const faqId = `${catIdx}-${idx}`;
                      return (
                        <div key={faqId} className="border border-background-200/60 bg-background-100/60">
                          <button
                            onClick={() => toggle(faqId)}
                            className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left hover:bg-background-100/50 transition-colors duration-200"
                            aria-expanded={openIndex === faqId}
                          >
                            <span className="faq-question font-heading text-[17px] text-foreground-900 tracking-[0.05em] pr-4">
                              {t(`faq.cat${catIdx}.${idx}.question`)}
                            </span>
                            <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                              <i
                                className={`text-sm text-accent-600 transition-transform duration-300 ${
                                  openIndex === faqId ? 'ri-subtract-line' : 'ri-add-line'
                                }`}
                              ></i>
                            </span>
                          </button>
                          <div
                            className={`overflow-hidden transition-all duration-300 ${
                              openIndex === faqId ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className="px-6 pb-5 pt-0">
                              <div className="border-t border-background-200/40 pt-4">
                                {renderParagraphs(String(t(`faq.cat${catIdx}.${idx}.answer`)), 'faq-answer text-[16px] text-foreground-700 leading-relaxed tracking-[0.03em]')}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-primary-900">
          <div className="max-w-[800px] mx-auto px-8 text-center">
            {renderParagraphs(String(t('faq.contact.label')), 'text-accent-400/60 text-[13px] tracking-[0.2em] mb-6 font-label')}
            <h2 className="font-heading text-xl md:text-2xl text-foreground-100 tracking-[0.06em] mb-4">
              {t('faq.contact.title')}
            </h2>
            <div className="gold-line mx-auto mb-8"></div>
            {renderParagraphs(String(t('faq.contact.body')), 'text-[16px] text-foreground-400 leading-relaxed tracking-[0.04em] mb-10')}
            <div className="flex flex-col sm:flex-row justify-center gap-5">
              <Link
                to="/contact"
                className="px-10 py-4 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap"
              >
                {t('faq.contact.formLink')}
              </Link>
              <a
                href="tel:0368244086"
                className="px-10 py-4 border border-accent-500/50 text-accent-300 hover:text-accent-200 hover:border-accent-400 hover:bg-accent-500/8 text-[15px] tracking-[0.1em] transition-all duration-300 whitespace-nowrap"
              >
                TEL: 03-6824-4086
              </a>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <section className="py-8 bg-background-50">
          <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 text-center">
            {renderParagraphs(String(t('faq.lastUpdated')), 'text-[13px] text-foreground-500 tracking-[0.05em]')}
          </div>
        </section>
      </main>

      <Footer />

      {/* Back to Top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
        aria-label={t('common.backToTop')}
      >
        <i className="ri-arrow-up-line"></i>
      </button>
    </>
  );
}
